import './TheFirstTake.css';
import TrackRow from './TrackRow';
import Tube, { TubeMethods } from './Tube';
import { Track } from './model/Track';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';
import { normalize } from './util/Normalize';
import { logPrefix } from './util/Log';
import { parseDate } from './util/Time';

export default function TubeMain({ tracksUrl }: { tracksUrl: string }) {
  const tubeMethodsRef = useRef<TubeMethods | null>(null);

  const [isTubuReady, setIsTubuReady] = useState<boolean>(false);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [unplayableTracks, setUnplayableTracks] = useState<Track[]>([]);
  const [curTrackId, setCurTrackId] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isRadio, setIsRadio] = useState<boolean>(false);

  function getNextTrack() {
    const curTrack = tracks.find(t => t.id === curTrackId);
    if (!curTrack) {
      console.warn(logPrefix(), `現在のトラックが見つからない id=${curTrackId}`);
      return;
    }
    let index = tracks.indexOf(curTrack);

    for (let count = 0; count < tracks.length + 3; count++) { // +3はおまけ
      index++;
      if (tracks.length <= index) {
        index = 0;
      }

      const track = tracks[index];
      if (track.enable && track.visible) {
        return track;
      }
    }
    return null;
  }

  function getPrevTrack() {
    const curTrack = tracks.find(t => t.id === curTrackId);
    if (!curTrack) {
      console.warn(logPrefix(), `現在のトラックが見つからない id=${curTrackId}`);
      return;
    }
    let index = tracks.indexOf(curTrack);

    for (let count = 0; count < tracks.length + 3; count++) { // +3はおまけ
      index--;
      if (index < 0) {
        index = tracks.length - 1;
      }

      const track = tracks[index];
      if (track.enable && track.visible) {
        return track;
      }
    }
    return null;
  }

  function sort(compFunc: (a: Track, b: Track) => number) {
    setTracks((prev) => [...prev.sort(compFunc)]);
  }

  function saveTracks() {
    // 初回を弾く セーブデータが空で上書きされるので
    if (tracks.length === 0) return;

    const json = JSON.stringify(tracks.map(t => ({ id: t.id, enable: t.enable, star: t.star, memo: t.memo })));
    localStorage.setItem('tft-Tracks', json);
  }

  function loadTracks(): Track[] {
    const json = localStorage.getItem('tft-Tracks');
    if (!json) {
      return [];
    }
    const storage = JSON.parse(json);
    if (!storage) {
      return [];
    }
    return storage;
  }

  function save(strageKey: string, value: string) {
    const json = JSON.stringify(value);
    localStorage.setItem('tft-' + strageKey, json);
  }

  function load(strageKey: string): string {
    const json = localStorage.getItem('tft-' + strageKey);
    if (!json) {
      return '';
    }
    const storage = JSON.parse(json);
    if (!storage) {
      return '';
    }
    return storage;
  }

  function handleTubeMethodsReady(methods: TubeMethods) {
    console.log(logPrefix(), 'handleTubeMethodsReady>');
    tubeMethodsRef.current = methods;
    setIsTubuReady(true);

    let curTrack = tracks.find(t => t.id === curTrackId);
    // もし現在のトラックが無ければ、適当な一つを選ぶ
    if (!curTrack) {
      curTrack = tracks.find(t => t.enable && t.visible);
    }
    if (!curTrack) {
      curTrack = tracks[0];
    }

    // Tubeと連携して、自動再生されない ロードするのみ
    playTrack(curTrack, false);
  };

  function handleChangeAllTrackEnable(ev: ChangeEvent<HTMLInputElement>) {
    setTracks(prevTracks => {
      const isChecked = ev.target.checked;
      const newTracks = prevTracks.map(track => {
        if (track.visible && track.enable !== isChecked) {
          track.enable = isChecked;
          return { ...track };
        } else {
          return track;
        }
      })
      return newTracks;
    });
  }

  const handleChangeTrack = useCallback((track: Track) => {
    setTracks((prevTracks) => {
      const newTrack = { ...track };
      const newTracks = [...prevTracks];
      newTracks[newTracks.indexOf(track)] = newTrack;
      return newTracks;
    });
  }, []);

  const handlePlay = useCallback(() => {
    console.debug(logPrefix(), 'handlePlay>');
    setIsPlaying(true);

    function scroll(trackId: string) {
      const curTrackTr = document.querySelector(`tr.track-${trackId}`);
      if (!curTrackTr) {
        return;
      }
      scrollIntoView(curTrackTr,
        {
          behavior: 'smooth',
          block: 'start',
          scrollMode: 'always',
          boundary: document.querySelector('.scrollBox')
        });
    }
    scroll(curTrackId);
  }, [curTrackId]);

  const handlePause = useCallback(() => {
    console.debug(logPrefix(), 'handlePause>');
    setIsPlaying(false);
  }, []);

  const handleTrackEnd = useCallback(() => {
    console.debug(logPrefix(), 'handleTrackEnd>');
    const nextTrack = getNextTrack();
    if (!nextTrack) {
      return;
    }
    playTrack(nextTrack, false);

    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [curTrackId, tracks]);

  const playTrack = useCallback((track: Track, quickly = true) => {
    console.log(logPrefix(), 'playTrack> title:' + track.title);
    setCurTrackId(track.id);
    tubeMethodsRef.current?.playTrack(track, quickly);
  }, []);

  //////////////////////////////// end of Function


  useEffect(() => {
    console.log(logPrefix(), 'TheFirstTake#useEffect[tracksUrl]>');

    const savedCurTrackId = load('curTrackId');
    setCurTrackId(savedCurTrackId);
    const savedSearch = load('search');
    setSearch(savedSearch);
    const savedTracks = loadTracks();
    fetch(tracksUrl + `?HOGE=${Date.now()}`)
      .then(res => res.json())
      .then((fetchedTracks: Track[]) => {
        console.log(logPrefix(), 'TheFirstTake#fetch-then>', fetchedTracks.length);
        fetchedTracks.forEach(t => t.releaseDate = t.releaseDateString ? new Date(t.releaseDateString) : new Date());

        fetchedTracks = fetchedTracks.sort((a, b) => b.releaseDate.getTime() - a.releaseDate.getTime());

        {
          const newTasks = fetchedTracks.filter(t => t.embedded === '不埋');
          setUnplayableTracks(newTasks);
        }

        {
          const fTracks = fetchedTracks.filter(t => t.embedded !== '不埋');

          const knownTracks = savedTracks
            .filter(s => fTracks.some(f => f.id === s.id)) // セーブデータにあってもフェッチデータに無ければ消す
            .map(s => {
              const f = fTracks.find(f => f.id === s.id) as Track;
              f.isNew = false;
              f.enable = s.enable;
              f.star = s.star;
              f.memo = s.memo;
              return f;
            });

          const unknownTracks = fTracks
            .filter(f => !savedTracks.some(s => s.id === f.id))
            .map(f => {
              f.isNew = true;
              f.enable = true;
              f.star = 0;
              f.memo = '';
              return f;
            });

          // 新しいトラックを先頭に配置
          const newTracks = [...unknownTracks, ...knownTracks];
          newTracks.forEach(a => a.searchWord = normalize(
            a.title + ' '
            + a.musician + ' '
            + a.videoId + ' '
          ));
          setTracks(newTracks);
        }
      })
      .catch((ex) => {
        const msg = '曲情報を取得できない。24時間後に来てね。エラー=' + ex.toString();
        console.error(logPrefix(), msg);
        alert(msg);
        return;
      });

    return () => {
      console.log(logPrefix(), 'TheFirstTake終了処理');
      tubeMethodsRef.current = null;
    }
  }, [tracksUrl]);

  useEffect(() => {
    saveTracks();
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [tracks]);

  useEffect(() => {
    save('curTrackId', curTrackId);
  }, [curTrackId]);

  useEffect(() => {
    save('search', search);
  }, [search]);

  //////////////////////////////////////// end of useEffect

  if (tracks.length === 0) {
    console.log(logPrefix(), 'TheFirstTake render(init)');
    return <>コショウ少々お待ち</>;
  }

  // フィルター処理
  {
    const s = normalize(search).trim();
    if (s.length === 0) {
      tracks.forEach(t => t.visible = true);
    } else {
      tracks.forEach(t => t.visible = (t.searchWord + ' ' + normalize(t.memo + ' ' + '★'.repeat(t.star))).includes(s));
    }
  }


  const curTrack = tracks.find(t => t.id === curTrackId);
  const isAllTrackEnable = tracks.filter(t => t.visible).every(t => t.enable);

  console.log(logPrefix(), 'TheFirstTake render');
  return (<>
    <main>
      <div className={isRadio ? 'invisible' : ''}>
        <Tube
          onMethodsReady={handleTubeMethodsReady}
          onTrackEnd={handleTrackEnd}
          onPlay={handlePlay}
          onPause={handlePause}
        />
      </div>

      <div className='trackInfo'>
        <div>{curTrack?.title}</div>
        <div>{curTrack?.musician}</div>
      </div>


      {isTubuReady && <>
        <div className='controlPanel'>

          <span title='前の動画を再生' className='clickable' onClick={() => {
            const track = getPrevTrack();
            if (!track) {
              return;
            }
            playTrack(track);
          }}>
            ⏮️
          </span>

          <span title='この動画を最初から再生' className='clickable' onClick={() => {
            const track = tracks.find(t => t.id === curTrackId);
            if (!track) {
              return;
            }
            playTrack(track);
          }}>
            ⏪
          </span>

          <span title='一時停止' className={'clickable major ' + (isPlaying ? '' : 'hidden')} onClick={() => {
            tubeMethodsRef.current?.pause();
          }}>
            ⏸️
          </span>

          <span title='再生' className={'clickable major ' + (isPlaying ? 'hidden' : '')} onClick={() => {
            tubeMethodsRef.current?.play();
          }}>
            ▶️
          </span>
          <span title='次の動画を再生' className='clickable' onClick={() => {
            const nextTrack = getNextTrack();

            if (!nextTrack) {
              return;
            }

            playTrack(nextTrack);
          }}>
            ⏭️
          </span>

          <span>|</span>

          <span title='音声のみに' className={'clickable ' + (isRadio ? 'hidden' : '')} onClick={() => {
            setIsRadio(true);
          }}>📻
          </span>

          <span title='音声と映像に' className={'clickable ' + (isRadio ? '' : 'hidden')} onClick={() => {
            setIsRadio(false);
          }}>📺
          </span>

          <span title='シャッフル' className='clickable' onClick={() => {
            setTracks(prev => {
              const newTracks = shuffle(prev);
              if (!isPlaying) {
                const newTrack = newTracks[0];
                playTrack(newTrack);
              }
              return newTracks;
            });
          }} >🔀</span>
        </div>
      </>}

      <div className='searchContainer'>
        <input type='text' className='search' placeholder='検索ワード'
          value={search} onChange={(ev) => setSearch(ev.target.value)} />
        <span>
          {tracks.filter(t => t.visible && t.enable).length}件
        </span>
      </div>

      <div className='scrollBox'>
        <table>
          <thead>
            <tr>
              <th className='checkbox' >
                <input type='checkbox' className='clickable' title='一括チェック'
                  onChange={handleChangeAllTrackEnable} checked={isAllTrackEnable}
                />
              </th>
              <th className='title'>
                タイトル<br />ミュージシャン
              </th>
              <th className='date'>
                公開日
                <br />
                <span className='clickable' onClick={() => sort((a, b) => b.releaseDate.getTime() - a.releaseDate.getTime())}>🔽</span>
                &#x3000;
                <span className='clickable' onClick={() => sort((a, b) => a.releaseDate.getTime() - b.releaseDate.getTime())}>🔼</span>
              </th>
              <th className='star' >
                星
                <br />
                <span className='clickable' onClick={() => sort((a, b) => b.star - a.star)}>🔽</span>
                &#x3000;
                <span className='clickable' onClick={() => sort((a, b) => a.star - b.star)}>🔼</span>
              </th>
              <th className='memo'>
                メモ
                <br />
                <span className='clickable' onClick={() => sort((a, b) => b.memo.localeCompare(a.memo))}>🔽</span>
                &#x3000;
                <span className='clickable' onClick={() => sort((a, b) => a.memo.localeCompare(b.memo))}>🔼</span>
              </th>

            </tr>
          </thead>
          <tbody>
            {tracks.filter(t => t.visible).map((t) => (
              <TrackRow key={t.id} track={t} isCurrent={t.id === curTrackId}
                playTrack={playTrack} onChangeTrack={handleChangeTrack} />
            ))}
          </tbody>
        </table>
      </div>
    </main >
    <div className='unplayable'>
      <div>YouTubeでしか見られない動画たち</div>

      <div className='scrollBox'>
        <table>
          {
            unplayableTracks.map((t) => {
              const { yyyy, mm, dd } = parseDate(t.releaseDate);
              return (
                <tr key={t.id}>
                  <td>{t.title}<br />{t.musician}</td>
                  <td>
                    <a target='_blank' rel='noreferrer' href={`https://www.youtube.com/watch?v=${t.videoId}&start=${t.startSeconds}&end=${t.endSeconds}`}>YouTube<br />で見る</a>
                  </td>
                  <td>
                    {t.isNew && <><span className='new'>NEW</span><br /></>}
                    {yyyy}/<br />{mm}/{dd}
                  </td>
                </tr>
              )
            })
          }
        </table>
      </div>
    </div>

  </>);
}
/////////////////////////////////////////////////////////////////////////////

function shuffle<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; 0 < i; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

